import { getCheck } from "@sscs/report/utils";
import { PortalCheckType, RLChecksReport } from "@sscs/types";
import { ProcessingStatus, Version } from "Types";

export type Check = {
  label: RLChecksReport.ScanCheckLabel | undefined;
  processingStatus: ProcessingStatus | undefined;
  description: string;
  checkType: PortalCheckType;
};

export type CheckStatus = "pass" | "fail" | "pending" | "none";

export const getCheckStatus = (check: Check): CheckStatus => {
  if (check.label?.toLowerCase().includes("pass")) return "pass";
  if (check.label?.toLowerCase().includes("fail")) return "fail";
  if (check.processingStatus?.code === "PROCESSING") return "pending";
  return "none";
};

export const getAllChecks = (version: Version): Check[] => {
  const versionReportSummary =
    version.artifacts_summary.version.latest_report_summary;
  const reproReportSummary =
    version.artifacts_summary.repro?.latest_report_summary;
  const checks: Check[] = [];

  // Order is extremely important and needs to be preserved
  // Follows report order of https://alt-confluence.rl.lan/display/SwA/CLI+application list command
  checks.push({
    label: getCheck(versionReportSummary?.scan_report.checks, "artifact-check")
      ?.label,
    processingStatus: version.artifacts_summary.version.current_status,
    description: "Software package analysis",
    checkType: "VERSION-REPORT",
  });

  // Portal currently supports exactly one diff in projects so we assume there can be only one
  // If there are none, we don't want to show it
  const diffCheck = getCheck(
    versionReportSummary?.scan_report.checks,
    "artifact-diff",
  );
  if (diffCheck) {
    checks.push({
      label: diffCheck.label,
      processingStatus: version.artifacts_summary.version.current_status,
      description: `Diff with: ${diffCheck.reference}`,
      checkType: "VERSION-DIFF",
    });
  }

  // If repro artifact has processing status, it means reproducible build exists
  // We'll get repro artifact scan, diff and repro check
  if (version.artifacts_summary.repro?.current_status) {
    checks.push({
      label: getCheck(reproReportSummary?.scan_report.checks, "artifact-check")
        ?.label,
      processingStatus: version.artifacts_summary.repro?.current_status,
      description: "Software package analysis: repro",
      checkType: "REPRO-REPORT",
    });

    checks.push({
      label: getCheck(reproReportSummary?.scan_report.checks, "artifact-diff")
        ?.label,
      processingStatus: version.artifacts_summary.repro?.current_status,
      description: "Diff with: repro",
      checkType: "REPRO-DIFF",
    });

    checks.push({
      label: getCheck(reproReportSummary?.scan_report.checks, "repro-check")
        ?.label,
      processingStatus: version.artifacts_summary.repro?.current_status,
      description: "Reproducible build check",
      checkType: "REPRO-CHECK",
    });
  }

  return checks;
};

type CheckStatistics = {
  passed: number;
  failed: number;
  pending: number;
};

export const getChecksStats = (checkList: Check[]): CheckStatistics => {
  let passed = 0;
  let failed = 0;
  let pending = 0;

  checkList.forEach((check) => {
    const status = getCheckStatus(check);
    if (status === "pass") {
      passed++;
    } else if (status === "fail") {
      failed++;
    } else if (status === "pending") {
      pending++;
    }
  });

  return { passed, failed, pending };
};

export const getChecksSummaryText = (
  stats: CheckStatistics,
): { title: string; subtitle: string } => {
  let title = "All checks have passed";
  let subtitle = `${stats.passed} passed checks`;

  if (stats.pending > 0) {
    const failedText = stats.failed > 0 ? `${stats.failed} failed` : "";
    const passedText = stats.passed > 0 ? `${stats.passed} passed` : "";

    title = "Some checks haven’t completed yet";
    subtitle = `${failedText}${stats.failed > 0 ? ", " : ""}${passedText} and ${
      stats.pending
    } pending checks`;
  } else if (stats.failed > 0) {
    title = "Some checks have failed";
    subtitle = `${stats.passed.toLocaleString()} passed and ${stats.failed.toLocaleString()} failed checks`;
  }

  return { title, subtitle };
};
